import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Save, Eye, Globe, History, Plus, Trash2, ArrowUp, ArrowDown,
  AlertTriangle, Loader2, RefreshCw,
} from "lucide-react";
import { AdminShell } from "../components/AdminShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api, apiError } from "../lib/api";

const SECTION_TABS = [
  ["hero", "Hero"], ["about", "Acerca"], ["venue", "Fecha y sede"], ["program", "Programa"],
  ["workshops", "Talleres"], ["pricing", "Precios"], ["faq", "FAQ"], ["contact", "Contacto y redes"],
  ["footer", "Footer y legal"], ["seo", "SEO"],
];

const SECTION_LABELS = {
  hero: "Hero principal", about: "Acerca del evento", venue: "Fecha, sede y ubicación",
  program: "Programa general", workshops: "Sección de talleres", pricing: "Precios y accesos",
  faq: "Preguntas frecuentes", contact: "Contacto y redes", footer: "Footer y legal", seo: "SEO y compartir",
};

const ACT_TYPES = [
  ["cena", "Cena"], ["evento_general", "Evento general"], ["taller", "Taller"], ["sesion", "Sesión"],
  ["receso", "Receso"], ["registro", "Registro"], ["otro", "Otro"],
];
const FAQ_CATS = [
  ["general", "General"], ["registro", "Registro"], ["pagos", "Pagos"], ["accesos", "Accesos"],
  ["talleres", "Talleres"], ["cuidado_infantil", "Cuidado infantil"], ["ubicacion", "Ubicación"],
];
const ICON_OPTS = ["compass", "graduation-cap", "users", "calendar", "map-pin", "presentation", "clock", "utensils"]
  .map((v) => ({ value: v, label: v }));
const LOGO_OPTS = [{ value: "full", label: "Logo completo" }, { value: "title", label: "Logo horizontal" }, { value: "icon", label: "Isotipo" }];

const Txt = ({ label, value, onChange, testId, placeholder, required, type = "text" }) => (
  <div>
    <label className="text-xs font-bold text-hz-navy">{label}{required ? " *" : ""}</label>
    <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="mt-1" placeholder={placeholder} />
  </div>
);

const Area = ({ label, value, onChange, testId, rows = 3 }) => (
  <div>
    <label className="text-xs font-bold text-hz-navy">{label}</label>
    <Textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="mt-1" />
  </div>
);

const Chk = ({ label, checked, onChange, testId }) => (
  <label className="flex items-center gap-2 py-1 text-sm font-semibold text-hz-ink">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} className="h-4 w-4 accent-hz-cyan" />
    {label}
  </label>
);

const Sel = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="text-xs font-bold text-hz-navy">{label}</label>
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="mt-1 w-full rounded-md border border-hz-gray/50 bg-white px-3 py-2 text-sm">
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const RowCtl = ({ onUp, onDown, onDel, testId, first, last }) => (
  <div className="flex items-center gap-1">
    <button type="button" onClick={onUp} disabled={first} data-testid={`${testId}-up`} aria-label="Subir" className="rounded border border-hz-gray/40 p-1.5 text-hz-navy disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
    <button type="button" onClick={onDown} disabled={last} data-testid={`${testId}-down`} aria-label="Bajar" className="rounded border border-hz-gray/40 p-1.5 text-hz-navy disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
    {onDel && <button type="button" onClick={onDel} data-testid={`${testId}-del`} aria-label="Eliminar" className="rounded border border-hz-error/40 p-1.5 text-hz-error"><Trash2 className="h-3.5 w-3.5" /></button>}
  </div>
);

const SectionHead = ({ title, enabled, onToggle, testId }) => (
  <div className="mb-4 flex items-center justify-between rounded-xl border border-hz-gray/40 bg-hz-bg px-4 py-3">
    <p className="text-sm font-bold text-hz-navy">{title}</p>
    <Chk label={enabled !== false ? "Visible en landing" : "Oculta"} checked={enabled !== false} onChange={onToggle} testId={testId} />
  </div>
);

/* ---------------- Editores por sección (module-level: evita pérdida de foco) ---------------- */

const HeroEditor = ({ sec, upd, event, onSync }) => (
  <div className="space-y-4">
    <SectionHead title="Hero principal" enabled={sec.enabled} onToggle={(v) => upd({ enabled: v })} testId="hero-enabled" />
    <div className="grid gap-4 sm:grid-cols-2">
      <Sel label="Logo" value={sec.logo_variant} onChange={(v) => upd({ logo_variant: v })} options={LOGO_OPTS} testId="hero-logo" />
      <Txt label="Etiqueta superior (opcional)" value={sec.top_label} onChange={(v) => upd({ top_label: v })} testId="hero-top-label" />
      <Txt label="Título principal" value={sec.title} onChange={(v) => upd({ title: v })} testId="hero-title-input" required />
      <Txt label="Lema / subtítulo" value={sec.slogan} onChange={(v) => upd({ slogan: v })} testId="hero-slogan-input" />
    </div>
    <Area label="Descripción corta" value={sec.description} onChange={(v) => upd({ description: v })} testId="hero-desc-input" />
    <div className="rounded-lg border border-hz-cyan/30 bg-hz-cyan/5 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold text-hz-ink/70">Fecha/sede oficiales del evento pueden insertarse con un clic.</p>
        <Button size="sm" variant="outline" onClick={onSync} data-testid="hero-sync-event-btn" className="font-semibold">Usar fecha y sede oficiales</Button>
      </div>
      {event?.start_date ? null : <p className="mt-1 text-xs text-hz-warning">Aún no hay fecha oficial configurada en el sistema.</p>}
    </div>
    <div className="grid gap-4 sm:grid-cols-3">
      <Txt label="Texto de fecha" value={sec.date_text} onChange={(v) => upd({ date_text: v })} testId="hero-date-input" />
      <Txt label="Texto de hora" value={sec.time_text} onChange={(v) => upd({ time_text: v })} testId="hero-time-input" />
      <Txt label="Texto de sede" value={sec.venue_text} onChange={(v) => upd({ venue_text: v })} testId="hero-venue-input" />
    </div>
    <div className="grid gap-4 sm:grid-cols-2">
      <Chk label="Mostrar botón principal (CTA)" checked={sec.show_cta !== false} onChange={(v) => upd({ show_cta: v })} testId="hero-show-cta" />
      <Chk label="Mostrar contador regresivo" checked={!!sec.show_countdown} onChange={(v) => upd({ show_countdown: v })} testId="hero-show-countdown" />
      <Txt label="Texto del botón principal" value={sec.cta_text} onChange={(v) => upd({ cta_text: v })} testId="hero-cta-text" required={sec.show_cta !== false} />
      <Txt label="Destino del botón principal" value={sec.cta_href} onChange={(v) => upd({ cta_href: v })} testId="hero-cta-href" placeholder="/registro" />
      <Txt label="Texto botón secundario (opcional)" value={sec.cta2_text} onChange={(v) => upd({ cta2_text: v })} testId="hero-cta2-text" />
      <Txt label="Destino botón secundario" value={sec.cta2_href} onChange={(v) => upd({ cta2_href: v })} testId="hero-cta2-href" placeholder="#programa" />
      {sec.show_countdown && <Txt label="Objetivo del contador (ISO)" value={sec.countdown_target} onChange={(v) => upd({ countdown_target: v })} testId="hero-countdown-target" placeholder="2026-11-14T09:00:00-06:00" />}
    </div>
    <div className="grid gap-4 sm:grid-cols-2">
      <Txt label="Imagen de fondo (URL opcional)" value={sec.bg_image_url} onChange={(v) => upd({ bg_image_url: v })} testId="hero-bg-url" />
      <Txt label="Texto alternativo de imagen" value={sec.bg_image_alt} onChange={(v) => upd({ bg_image_alt: v })} testId="hero-bg-alt" />
    </div>
  </div>
);

const AboutEditor = ({ sec, upd }) => {
  const cards = sec.cards || [];
  const setCards = (next) => upd({ cards: next });
  const move = (i, dir) => { const n = [...cards]; const j = i + dir; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; setCards(n); };
  return (
    <div className="space-y-4">
      <SectionHead title="Acerca del evento" enabled={sec.enabled} onToggle={(v) => upd({ enabled: v })} testId="about-enabled" />
      <div className="grid gap-4 sm:grid-cols-2">
        <Txt label="Etiqueta" value={sec.label} onChange={(v) => upd({ label: v })} testId="about-label" />
        <Txt label="Título" value={sec.title} onChange={(v) => upd({ title: v })} testId="about-title-input" required />
      </div>
      <Area label="Texto descriptivo" value={sec.text} onChange={(v) => upd({ text: v })} testId="about-text-input" rows={4} />
      <div className="space-y-3">
        <p className="text-xs font-bold text-hz-navy">Tarjetas (máximo 6)</p>
        {cards.map((card, i) => (
          <div key={i} className="rounded-xl border border-hz-gray/40 p-3" data-testid={`about-card-edit-${i}`}>
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold text-hz-ink/70">Tarjeta {i + 1}</p>
              <RowCtl testId={`about-card-${i}`} first={i === 0} last={i === cards.length - 1}
                onUp={() => move(i, -1)} onDown={() => move(i, 1)} onDel={() => setCards(cards.filter((_, j) => j !== i))} />
            </div>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <Txt label="Título" value={card.title} onChange={(v) => setCards(cards.map((x, j) => j === i ? { ...x, title: v } : x))} testId={`about-card-title-${i}`} />
              <Sel label="Ícono" value={card.icon} onChange={(v) => setCards(cards.map((x, j) => j === i ? { ...x, icon: v } : x))} options={ICON_OPTS} testId={`about-card-icon-${i}`} />
            </div>
            <div className="mt-2">
              <Area label="Descripción" value={card.text} onChange={(v) => setCards(cards.map((x, j) => j === i ? { ...x, text: v } : x))} testId={`about-card-text-${i}`} rows={2} />
            </div>
            <Chk label="Activa" checked={card.active !== false} onChange={(v) => setCards(cards.map((x, j) => j === i ? { ...x, active: v } : x))} testId={`about-card-active-${i}`} />
          </div>
        ))}
        {cards.length < 6 && (
          <Button size="sm" variant="outline" onClick={() => setCards([...cards, { icon: "compass", title: "", text: "", order: cards.length, active: true }])} data-testid="about-card-add" className="font-semibold">
            <Plus className="mr-1 h-4 w-4" /> Agregar tarjeta
          </Button>
        )}
      </div>
    </div>
  );
};

const VenueEditor = ({ sec, upd, onSync }) => (
  <div className="space-y-4">
    <SectionHead title="Fecha, sede y ubicación" enabled={sec.enabled} onToggle={(v) => upd({ enabled: v })} testId="venue-enabled" />
    <div className="flex items-center justify-between gap-2">
      <Txt label="Título de sección" value={sec.title} onChange={(v) => upd({ title: v })} testId="venue-title-input" />
      <Button size="sm" variant="outline" onClick={onSync} data-testid="venue-sync-event-btn" className="mt-4 shrink-0 font-semibold">Sincronizar fechas del evento</Button>
    </div>
    <div className="grid gap-4 sm:grid-cols-3">
      <Txt label="Fecha viernes" value={sec.friday_date} onChange={(v) => upd({ friday_date: v })} testId="venue-fri-date" placeholder="13 de noviembre de 2026" />
      <Txt label="Hora inicio viernes" value={sec.friday_start} onChange={(v) => upd({ friday_start: v })} testId="venue-fri-start" placeholder="18:00" />
      <Txt label="Hora fin viernes" value={sec.friday_end} onChange={(v) => upd({ friday_end: v })} testId="venue-fri-end" placeholder="22:00" />
      <Txt label="Fecha sábado" value={sec.saturday_date} onChange={(v) => upd({ saturday_date: v })} testId="venue-sat-date" placeholder="14 de noviembre de 2026" />
      <Txt label="Hora inicio sábado" value={sec.saturday_start} onChange={(v) => upd({ saturday_start: v })} testId="venue-sat-start" placeholder="09:00" />
      <Txt label="Hora fin sábado" value={sec.saturday_end} onChange={(v) => upd({ saturday_end: v })} testId="venue-sat-end" placeholder="18:00" />
    </div>
    <div className="grid gap-4 sm:grid-cols-2">
      <Txt label="Nombre de sede" value={sec.venue_name} onChange={(v) => upd({ venue_name: v })} testId="venue-name" />
      <Txt label="Ciudad / estado" value={sec.city_state} onChange={(v) => upd({ city_state: v })} testId="venue-city" />
    </div>
    <Txt label="Dirección completa" value={sec.address} onChange={(v) => upd({ address: v })} testId="venue-address" />
    <div className="grid gap-4 sm:grid-cols-2">
      <Area label="Indicaciones adicionales" value={sec.directions} onChange={(v) => upd({ directions: v })} testId="venue-directions" rows={2} />
      <Area label="Estacionamiento (opcional)" value={sec.parking_info} onChange={(v) => upd({ parking_info: v })} testId="venue-parking" rows={2} />
      <Txt label="URL de Google Maps" value={sec.maps_url} onChange={(v) => upd({ maps_url: v })} testId="venue-maps-url" />
      <Txt label="URL de mapa embebido (opcional)" value={sec.maps_embed_url} onChange={(v) => upd({ maps_embed_url: v })} testId="venue-embed-url" />
      <Txt label="Teléfono de contacto de sede (opcional)" value={sec.phone} onChange={(v) => upd({ phone: v })} testId="venue-phone" />
      <Txt label="Imagen de sede (URL opcional)" value={sec.image_url} onChange={(v) => upd({ image_url: v })} testId="venue-image-url" />
      <Txt label="Texto alternativo de imagen" value={sec.image_alt} onChange={(v) => upd({ image_alt: v })} testId="venue-image-alt" />
    </div>
  </div>
);

const ProgramEditor = ({ sec, upd, workshops }) => {
  const acts = sec.activities || [];
  const setActs = (next) => upd({ activities: next });
  const move = (i, dir) => { const n = [...acts]; const j = i + dir; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; setActs(n); };
  const patch = (i, p) => setActs(acts.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const syncWs = (i, wid) => {
    const w = workshops.find((x) => x.id === wid);
    if (!w) { patch(i, { workshop_id: "" }); return; }
    const st = w.starts_at ? String(w.starts_at).slice(11, 16) : "";
    const en = w.ends_at ? String(w.ends_at).slice(11, 16) : "";
    patch(i, { workshop_id: wid, title: w.name, room: w.room_name || "", start_time: st, end_time: en });
    toast.info(`Actividad sincronizada con el taller "${w.name}".`);
  };
  return (
    <div className="space-y-4">
      <SectionHead title="Programa general" enabled={sec.enabled} onToggle={(v) => upd({ enabled: v })} testId="program-enabled" />
      <div className="grid gap-4 sm:grid-cols-2">
        <Txt label="Etiqueta" value={sec.label} onChange={(v) => upd({ label: v })} testId="program-label" />
        <Txt label="Título" value={sec.title} onChange={(v) => upd({ title: v })} testId="program-title-input" required />
      </div>
      <Area label="Texto introductorio (opcional)" value={sec.intro_text} onChange={(v) => upd({ intro_text: v })} testId="program-intro" rows={2} />
      <div className="space-y-3">
        <p className="text-xs font-bold text-hz-navy">Actividades (se ordenan por día y hora en la landing)</p>
        {acts.map((a, i) => (
          <div key={a.id || i} className="rounded-xl border border-hz-gray/40 p-3" data-testid={`program-act-edit-${i}`}>
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold text-hz-ink/70">Actividad {i + 1}</p>
              <RowCtl testId={`program-act-${i}`} first={i === 0} last={i === acts.length - 1}
                onUp={() => move(i, -1)} onDown={() => move(i, 1)} onDel={() => setActs(acts.filter((_, j) => j !== i))} />
            </div>
            <div className="mt-2 grid gap-3 sm:grid-cols-3">
              <Sel label="Día" value={a.day} onChange={(v) => patch(i, { day: v })} testId={`program-act-day-${i}`}
                options={[{ value: "friday", label: "Viernes" }, { value: "saturday", label: "Sábado" }]} />
              <Sel label="Tipo" value={a.type} onChange={(v) => patch(i, { type: v })} testId={`program-act-type-${i}`}
                options={ACT_TYPES.map(([value, label]) => ({ value, label }))} />
              <Txt label="Fecha visible (opcional)" value={a.date_text} onChange={(v) => patch(i, { date_text: v })} testId={`program-act-date-${i}`} placeholder="13 de noviembre" />
              <Txt label="Hora inicio" value={a.start_time} onChange={(v) => patch(i, { start_time: v })} testId={`program-act-start-${i}`} placeholder="18:00" />
              <Txt label="Hora fin" value={a.end_time} onChange={(v) => patch(i, { end_time: v })} testId={`program-act-end-${i}`} placeholder="22:00" />
              <Txt label="Título" value={a.title} onChange={(v) => patch(i, { title: v })} testId={`program-act-title-${i}`} />
              <Txt label="Salón/ubicación (opcional)" value={a.room} onChange={(v) => patch(i, { room: v })} testId={`program-act-room-${i}`} />
              <Txt label="Etiqueta (opcional)" value={a.label} onChange={(v) => patch(i, { label: v })} testId={`program-act-label-${i}`} />
              <Txt label="Enlace (opcional)" value={a.link} onChange={(v) => patch(i, { link: v })} testId={`program-act-link-${i}`} />
            </div>
            {a.type === "taller" && (
              <div className="mt-2">
                <Sel label="Vincular taller existente (sincroniza nombre/horario/salón)" value={a.workshop_id || ""} onChange={(v) => syncWs(i, v)} testId={`program-act-ws-${i}`}
                  options={[{ value: "", label: "Sin vincular" }, ...workshops.map((w) => ({ value: w.id, label: w.name }))]} />
              </div>
            )}
            <div className="mt-2">
              <Area label="Descripción" value={a.description} onChange={(v) => patch(i, { description: v })} testId={`program-act-desc-${i}`} rows={2} />
            </div>
            <Chk label="Activa" checked={a.active !== false} onChange={(v) => patch(i, { active: v })} testId={`program-act-active-${i}`} />
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={() => setActs([...acts, { id: `act-${Date.now()}`, day: "friday", title: "", description: "", type: "otro", start_time: "", end_time: "", room: "", label: "", link: "", workshop_id: "", order: acts.length, active: true }])} data-testid="program-act-add" className="font-semibold">
          <Plus className="mr-1 h-4 w-4" /> Agregar actividad
        </Button>
      </div>
    </div>
  );
};

const WorkshopsEditor = ({ sec, upd }) => (
  <div className="space-y-4">
    <SectionHead title="Sección de talleres" enabled={sec.enabled} onToggle={(v) => upd({ enabled: v })} testId="workshops-enabled" />
    <p className="rounded-lg border border-hz-cyan/30 bg-hz-cyan/5 px-3 py-2 text-xs text-hz-ink/70">
      Los talleres se toman del catálogo oficial (módulo Talleres). Solo se configura su presentación aquí.
    </p>
    <div className="grid gap-4 sm:grid-cols-2">
      <Txt label="Etiqueta" value={sec.label} onChange={(v) => upd({ label: v })} testId="ws-label" />
      <Txt label="Título de sección" value={sec.title} onChange={(v) => upd({ title: v })} testId="ws-title" required />
      <Txt label="Cantidad máxima a mostrar" type="number" value={sec.max_items} onChange={(v) => upd({ max_items: Number(v) || 12 })} testId="ws-max" />
      <Sel label="Orden" value={sec.sort_by} onChange={(v) => upd({ sort_by: v })} testId="ws-sort"
        options={[{ value: "schedule", label: "Por horario" }, { value: "name", label: "Por nombre" }]} />
      <Txt label="Texto del CTA (opcional)" value={sec.cta_text} onChange={(v) => upd({ cta_text: v })} testId="ws-cta-text" />
      <Txt label="Destino del CTA" value={sec.cta_href} onChange={(v) => upd({ cta_href: v })} testId="ws-cta-href" placeholder="/registro" />
    </div>
    <Area label="Texto introductorio" value={sec.intro_text} onChange={(v) => upd({ intro_text: v })} testId="ws-intro" rows={2} />
    <div className="grid gap-1 sm:grid-cols-2">
      <Chk label="Mostrar precio" checked={sec.show_price !== false} onChange={(v) => upd({ show_price: v })} testId="ws-show-price" />
      <Chk label="Mostrar cupo" checked={sec.show_capacity !== false} onChange={(v) => upd({ show_capacity: v })} testId="ws-show-capacity" />
      <Chk label="Mostrar ponente" checked={sec.show_speaker !== false} onChange={(v) => upd({ show_speaker: v })} testId="ws-show-speaker" />
      <Chk label="Mostrar salón" checked={sec.show_room !== false} onChange={(v) => upd({ show_room: v })} testId="ws-show-room" />
    </div>
    <Txt label="Mensaje cuando no haya talleres" value={sec.empty_text} onChange={(v) => upd({ empty_text: v })} testId="ws-empty" />
  </div>
);

const PricingEditor = ({ sec, upd }) => (
  <div className="space-y-4">
    <SectionHead title="Precios y accesos" enabled={sec.enabled} onToggle={(v) => upd({ enabled: v })} testId="pricing-enabled" />
    <p className="rounded-lg border border-hz-cyan/30 bg-hz-cyan/5 px-3 py-2 text-xs text-hz-ink/70">
      Los montos se toman de /admin → Precios. Nunca se editan aquí; solo la presentación.
    </p>
    <div className="grid gap-4 sm:grid-cols-2">
      <Txt label="Etiqueta" value={sec.label} onChange={(v) => upd({ label: v })} testId="price-label" />
      <Txt label="Título de sección" value={sec.title} onChange={(v) => upd({ title: v })} testId="price-title" required />
      <Txt label="Texto del CTA" value={sec.cta_text} onChange={(v) => upd({ cta_text: v })} testId="price-cta-text" />
      <Txt label="Destino del CTA" value={sec.cta_href} onChange={(v) => upd({ cta_href: v })} testId="price-cta-href" placeholder="/registro" />
    </div>
    <Area label="Texto explicativo" value={sec.intro_text} onChange={(v) => upd({ intro_text: v })} testId="price-intro" rows={2} />
    <div className="grid gap-4 sm:grid-cols-3">
      <Txt label="Nota de pago grupal" value={sec.group_note} onChange={(v) => upd({ group_note: v })} testId="price-group-note" />
      <Txt label="Nota sobre confirmación de pago" value={sec.payment_note} onChange={(v) => upd({ payment_note: v })} testId="price-payment-note" />
      <Txt label="Nota sobre QR" value={sec.qr_note} onChange={(v) => upd({ qr_note: v })} testId="price-qr-note" />
    </div>
    <Chk label='Mostrar "desde" en talleres' checked={!!sec.show_from} onChange={(v) => upd({ show_from: v })} testId="price-show-from" />
  </div>
);

const FaqEditor = ({ sec, upd }) => {
  const items = sec.items || [];
  const setItems = (next) => upd({ items: next });
  const [q, setQ] = useState("");
  const move = (i, dir) => { const n = [...items]; const j = i + dir; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; setItems(n); };
  const patch = (i, p) => setItems(items.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const shown = items.map((it, i) => ({ it, i })).filter(({ it }) => !q || it.question.toLowerCase().includes(q.toLowerCase()) || (it.category || "").includes(q.toLowerCase()));
  return (
    <div className="space-y-4">
      <SectionHead title="Preguntas frecuentes" enabled={sec.enabled} onToggle={(v) => upd({ enabled: v })} testId="faq-enabled" />
      <div className="grid gap-4 sm:grid-cols-2">
        <Txt label="Etiqueta" value={sec.label} onChange={(v) => upd({ label: v })} testId="faq-label" />
        <Txt label="Título" value={sec.title} onChange={(v) => upd({ title: v })} testId="faq-title-input" required />
      </div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por pregunta o categoría" data-testid="faq-search" />
      <div className="space-y-3">
        {shown.map(({ it, i }) => (
          <div key={it.id || i} className="rounded-xl border border-hz-gray/40 p-3" data-testid={`faq-edit-${i}`}>
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold text-hz-ink/70">FAQ {i + 1}</p>
              <RowCtl testId={`faq-item-ctl-${i}`} first={i === 0} last={i === items.length - 1}
                onUp={() => move(i, -1)} onDown={() => move(i, 1)} onDel={() => setItems(items.filter((_, j) => j !== i))} />
            </div>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <Txt label="Pregunta" value={it.question} onChange={(v) => patch(i, { question: v })} testId={`faq-question-${i}`} />
              <Sel label="Categoría" value={it.category} onChange={(v) => patch(i, { category: v })} testId={`faq-category-${i}`}
                options={FAQ_CATS.map(([value, label]) => ({ value, label }))} />
            </div>
            <div className="mt-2">
              <Area label="Respuesta" value={it.answer} onChange={(v) => patch(i, { answer: v })} testId={`faq-answer-${i}`} rows={3} />
            </div>
            <Chk label="Activa" checked={it.active !== false} onChange={(v) => patch(i, { active: v })} testId={`faq-active-${i}`} />
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={() => setItems([...items, { id: `faq-${Date.now()}`, question: "", answer: "", category: "general", order: items.length, active: true }])} data-testid="faq-add" className="font-semibold">
          <Plus className="mr-1 h-4 w-4" /> Agregar pregunta
        </Button>
      </div>
    </div>
  );
};

const ContactEditor = ({ sec, upd }) => {
  const extras = sec.extra_socials || [];
  const setExtras = (next) => upd({ extra_socials: next });
  const move = (i, dir) => { const n = [...extras]; const j = i + dir; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; setExtras(n); };
  return (
    <div className="space-y-4">
      <SectionHead title="Contacto y redes sociales" enabled={sec.enabled} onToggle={(v) => upd({ enabled: v })} testId="contact-enabled" />
      <div className="grid gap-4 sm:grid-cols-2">
        <Txt label="Título de sección" value={sec.title} onChange={(v) => upd({ title: v })} testId="contact-title-input" />
        <Txt label="Correo principal de contacto" value={sec.email} onChange={(v) => upd({ email: v })} testId="contact-email" type="email" />
        <Txt label="Número de WhatsApp (formato internacional)" value={sec.whatsapp} onChange={(v) => upd({ whatsapp: v })} testId="contact-whatsapp" placeholder="5215512345678" />
        <Txt label="Mensaje de WhatsApp prellenado (opcional)" value={sec.whatsapp_message} onChange={(v) => upd({ whatsapp_message: v })} testId="contact-wa-msg" />
        <Txt label="Teléfono (opcional)" value={sec.phone} onChange={(v) => upd({ phone: v })} testId="contact-phone" />
        <Txt label="Horario de atención" value={sec.schedule} onChange={(v) => upd({ schedule: v })} testId="contact-schedule" />
        <Txt label="Responsable / equipo (opcional)" value={sec.team_name} onChange={(v) => upd({ team_name: v })} testId="contact-team" />
      </div>
      <Area label="Texto de atención" value={sec.text} onChange={(v) => upd({ text: v })} testId="contact-text" rows={2} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Txt label="Instagram URL" value={sec.instagram_url} onChange={(v) => upd({ instagram_url: v })} testId="social-instagram" />
        <Txt label="Facebook URL" value={sec.facebook_url} onChange={(v) => upd({ facebook_url: v })} testId="social-facebook" />
        <Txt label="YouTube URL" value={sec.youtube_url} onChange={(v) => upd({ youtube_url: v })} testId="social-youtube" />
        <Txt label="TikTok URL" value={sec.tiktok_url} onChange={(v) => upd({ tiktok_url: v })} testId="social-tiktok" />
        <Txt label="Sitio web URL" value={sec.website_url} onChange={(v) => upd({ website_url: v })} testId="social-website" />
      </div>
      <div className="space-y-3">
        <p className="text-xs font-bold text-hz-navy">Otras redes</p>
        {extras.map((s, i) => (
          <div key={i} className="flex flex-wrap items-end gap-3 rounded-xl border border-hz-gray/40 p-3" data-testid={`extra-social-${i}`}>
            <Txt label="Nombre" value={s.name} onChange={(v) => setExtras(extras.map((x, j) => j === i ? { ...x, name: v } : x))} testId={`extra-social-name-${i}`} />
            <Txt label="URL" value={s.url} onChange={(v) => setExtras(extras.map((x, j) => j === i ? { ...x, url: v } : x))} testId={`extra-social-url-${i}`} />
            <Chk label="Activa" checked={s.active !== false} onChange={(v) => setExtras(extras.map((x, j) => j === i ? { ...x, active: v } : x))} testId={`extra-social-active-${i}`} />
            <RowCtl testId={`extra-social-ctl-${i}`} first={i === 0} last={i === extras.length - 1}
              onUp={() => move(i, -1)} onDown={() => move(i, 1)} onDel={() => setExtras(extras.filter((_, j) => j !== i))} />
          </div>
        ))}
        {extras.length < 10 && (
          <Button size="sm" variant="outline" onClick={() => setExtras([...extras, { name: "", url: "", icon: "share2", order: extras.length, active: true }])} data-testid="extra-social-add" className="font-semibold">
            <Plus className="mr-1 h-4 w-4" /> Agregar red
          </Button>
        )}
      </div>
    </div>
  );
};

const FooterEditor = ({ sec, upd }) => {
  const links = sec.extra_links || [];
  const setLinks = (next) => upd({ extra_links: next });
  return (
    <div className="space-y-4">
      <div className="grid gap-1 sm:grid-cols-2">
        <Chk label="Mostrar logo en footer" checked={sec.show_logo !== false} onChange={(v) => upd({ show_logo: v })} testId="footer-show-logo" />
        <Chk label='Mostrar módulo "Acceso al equipo organizador"' checked={sec.staff_access_enabled !== false} onChange={(v) => upd({ staff_access_enabled: v })} testId="footer-staff-access" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Txt label="Texto de copyright" value={sec.copyright_text} onChange={(v) => upd({ copyright_text: v })} testId="footer-copyright" />
        <Txt label="Texto institucional (opcional)" value={sec.institutional_text} onChange={(v) => upd({ institutional_text: v })} testId="footer-institutional" />
        <Txt label='Texto "Información sujeta a actualización"' value={sec.update_note} onChange={(v) => upd({ update_note: v })} testId="footer-update-note" />
        <Txt label="URL aviso de privacidad (opcional)" value={sec.privacy_url} onChange={(v) => upd({ privacy_url: v })} testId="footer-privacy-url" />
        <Txt label="URL términos/condiciones (opcional)" value={sec.terms_url} onChange={(v) => upd({ terms_url: v })} testId="footer-terms-url" />
      </div>
      <Area label="Aviso de privacidad (texto corto)" value={sec.privacy_short} onChange={(v) => upd({ privacy_short: v })} testId="footer-privacy-short" rows={2} />
      <div className="space-y-3">
        <p className="text-xs font-bold text-hz-navy">Enlaces adicionales</p>
        {links.map((l, i) => (
          <div key={i} className="flex flex-wrap items-end gap-3 rounded-xl border border-hz-gray/40 p-3">
            <Txt label="Etiqueta" value={l.label} onChange={(v) => setLinks(links.map((x, j) => j === i ? { ...x, label: v } : x))} testId={`footer-link-label-${i}`} />
            <Txt label="URL" value={l.url} onChange={(v) => setLinks(links.map((x, j) => j === i ? { ...x, url: v } : x))} testId={`footer-link-url-${i}`} />
            <Chk label="Activo" checked={l.active !== false} onChange={(v) => setLinks(links.map((x, j) => j === i ? { ...x, active: v } : x))} testId={`footer-link-active-${i}`} />
            <button type="button" onClick={() => setLinks(links.filter((_, j) => j !== i))} data-testid={`footer-link-del-${i}`} aria-label="Eliminar" className="rounded border border-hz-error/40 p-1.5 text-hz-error"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={() => setLinks([...links, { label: "", url: "", order: links.length, active: true }])} data-testid="footer-link-add" className="font-semibold">
          <Plus className="mr-1 h-4 w-4" /> Agregar enlace
        </Button>
      </div>
    </div>
  );
};

const SeoEditor = ({ sec, upd }) => (
  <div className="space-y-4">
    <p className="rounded-lg border border-hz-warning/40 bg-hz-warning/5 px-3 py-2 text-xs text-hz-ink/70">
      Título y descripción SEO son obligatorios para publicar. El JSON-LD de evento se genera automáticamente cuando fecha y sede estén completas.
    </p>
    <Txt label="Título SEO" value={sec.title} onChange={(v) => upd({ title: v })} testId="seo-title" required />
    <Area label="Descripción SEO" value={sec.description} onChange={(v) => upd({ description: v })} testId="seo-description" rows={2} />
    <div className="grid gap-4 sm:grid-cols-2">
      <Txt label="URL canónica (opcional)" value={sec.canonical_url} onChange={(v) => upd({ canonical_url: v })} testId="seo-canonical" />
      <Txt label="Palabras clave (opcional)" value={sec.keywords} onChange={(v) => upd({ keywords: v })} testId="seo-keywords" />
      <Txt label="Imagen Open Graph (URL opcional)" value={sec.og_image_url} onChange={(v) => upd({ og_image_url: v })} testId="seo-og-image" />
      <Txt label="Texto alternativo de imagen OG" value={sec.og_image_alt} onChange={(v) => upd({ og_image_alt: v })} testId="seo-og-alt" />
    </div>
  </div>
);

/* ---------------- Módulo principal ---------------- */

const AdminLanding = () => (
  <AdminShell roles={["admin"]} title="Contenido de Landing Page" subtitle="Borrador, vista previa y publicación" testId="admin-landing">
    {() => <LandingBody />}
  </AdminShell>
);

const LandingBody = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [published, setPublished] = useState(null);
  const [meta, setMeta] = useState(null);
  const [event, setEvent] = useState(null);
  const [workshops, setWorkshops] = useState([]);
  const [tab, setTab] = useState("hero");
  const [dirty, setDirty] = useState(false);
  const [draftMatches, setDraftMatches] = useState(false);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState("");
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishConfirm, setPublishConfirm] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [restoreId, setRestoreId] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/landing").then((r) => {
      setContent(r.data.draft?.content_json || r.data.published?.content || null);
      setMeta({ updated_at: r.data.draft?.updated_at, summary: r.data.draft?.change_summary, editor: r.data.draft_editor });
      setPublished(r.data.published || null);
      setEvent(r.data.event || null);
      setDraftMatches(!!r.data.draft_matches_published);
    }).catch((e) => toast.error(apiError(e)));
    api.get("/admin/workshops").then((r) => setWorkshops(r.data.workshops || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const upd = useCallback((key, patch) => {
    setContent((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
    setDirty(true);
  }, []);

  const changedSections = useMemo(() => {
    if (!content || !published?.content) return [];
    return Object.keys(SECTION_LABELS).filter((k) => JSON.stringify(content[k] || {}) !== JSON.stringify(published.content[k] || {}));
  }, [content, published]);

  const alerts = useMemo(() => {
    if (!content) return [];
    const a = [];
    const hero = content.hero || {};
    if (!hero.date_text || /próximamente/i.test(hero.date_text)) a.push("Falta fecha oficial visible en el hero.");
    if (!(content.venue || {}).venue_name) a.push("Falta configurar la sede.");
    const ct = content.contact || {};
    if (ct.enabled && !ct.email && !ct.whatsapp) a.push("La sección de contacto está activa sin correo ni WhatsApp.");
    if (!((content.faq || {}).items || []).some((f) => f.active !== false)) a.push("No hay preguntas frecuentes activas.");
    return a;
  }, [content]);

  const saveDraft = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/landing/draft", { content, change_summary: summary || undefined });
      toast.success("Borrador guardado. La landing pública no cambió.");
      setMeta((m) => ({ ...m, updated_at: r.data.saved_at }));
      setDirty(false);
      setSummary("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const doPublish = async () => {
    try {
      const r = await api.post("/admin/landing/publish", { confirm: true, change_summary: summary || undefined });
      if (r.data.already_current) {
        toast.info(`El borrador ya es la versión publicada (v${r.data.published_version}). No se creó versión nueva.`);
      } else {
        toast.success(`Landing publicada correctamente — versión v${r.data.published_version}`, {
          action: { label: "Ver landing publicada", onClick: () => window.open("/", "_blank", "noopener") },
        });
      }
      setPublishOpen(false);
      setPublishConfirm(false);
      setDirty(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openHistory = async () => {
    try {
      const r = await api.get("/admin/landing/history");
      setHistory(r.data.versions || []);
      setHistoryOpen(true);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doRestore = async () => {
    try {
      await api.post(`/admin/landing/restore/${restoreId}`, { confirm: true });
      toast.success(`Versión ${restoreId} restaurada como borrador. Revisa y publica cuando esté lista.`);
      setRestoreId(null);
      setHistoryOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const syncHeroFromEvent = () => {
    if (!event?.start_date) { toast.error("No hay fecha oficial configurada en el sistema."); return; }
    const d = new Date(event.start_date);
    upd("hero", {
      date_text: d.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "America/Mexico_City" }),
      venue_text: event.venue || "",
    });
    toast.info("Fecha y sede oficiales insertadas en el borrador.");
  };

  const syncVenueFromEvent = () => {
    if (!event?.start_date) { toast.error("No hay fecha oficial configurada en el sistema."); return; }
    const fmt = (iso) => new Date(iso).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric", timeZone: "America/Mexico_City" });
    const tm = (iso) => String(iso).slice(11, 16);
    upd("venue", {
      friday_date: fmt(event.start_date),
      saturday_date: fmt(event.end_date || event.start_date),
      saturday_start: tm(event.start_date) || "",
      venue_name: event.venue || "",
    });
    toast.info("Fechas del evento sincronizadas en el borrador.");
  };

  if (!content) {
    return <div className="flex items-center justify-center py-20 text-sm font-semibold text-hz-ink/60"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando contenido…</div>;
  }

  const editors = {
    hero: <HeroEditor sec={content.hero || {}} upd={(p) => upd("hero", p)} event={event} onSync={syncHeroFromEvent} />,
    about: <AboutEditor sec={content.about || {}} upd={(p) => upd("about", p)} />,
    venue: <VenueEditor sec={content.venue || {}} upd={(p) => upd("venue", p)} onSync={syncVenueFromEvent} />,
    program: <ProgramEditor sec={content.program || {}} upd={(p) => upd("program", p)} workshops={workshops} />,
    workshops: <WorkshopsEditor sec={content.workshops || {}} upd={(p) => upd("workshops", p)} />,
    pricing: <PricingEditor sec={content.pricing || {}} upd={(p) => upd("pricing", p)} />,
    faq: <FaqEditor sec={content.faq || {}} upd={(p) => upd("faq", p)} />,
    contact: <ContactEditor sec={content.contact || {}} upd={(p) => upd("contact", p)} />,
    footer: <FooterEditor sec={content.footer || {}} upd={(p) => upd("footer", p)} />,
    seo: <SeoEditor sec={content.seo || {}} upd={(p) => upd("seo", p)} />,
  };

  return (
    <div className="space-y-5" data-testid="landing-cms">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-hz-gray/40 bg-white p-4">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className={`rounded-full px-3 py-1 font-bold ${dirty || !draftMatches ? "bg-hz-warning/15 text-hz-warning" : "bg-hz-success/10 text-hz-success"}`} data-testid="landing-draft-status">
            {dirty ? "Borrador con cambios sin guardar" : draftMatches ? "Borrador = versión publicada" : "Hay cambios sin publicar"}
          </span>
          {published && <span className="rounded-full bg-hz-navy/5 px-3 py-1 font-semibold text-hz-navy" data-testid="landing-published-version">Publicado: v{published.version}</span>}
          {meta?.updated_at && (
            <span className="text-hz-ink/60" data-testid="landing-last-saved">
              Último guardado: {new Date(meta.updated_at).toLocaleString("es-MX")}{meta.editor ? ` · ${meta.editor}` : ""}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={saveDraft} disabled={saving} data-testid="landing-save-draft" className="font-semibold">
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} Guardar borrador
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate("/admin/landing/preview")} data-testid="landing-preview-btn" className="font-semibold">
            <Eye className="mr-1 h-4 w-4" /> Vista previa
          </Button>
          <Button variant="outline" size="sm" onClick={openHistory} data-testid="landing-history-btn" className="font-semibold">
            <History className="mr-1 h-4 w-4" /> Historial
          </Button>
          <Button size="sm" onClick={() => { setPublishOpen(true); setPublishConfirm(false); }} data-testid="landing-publish-btn" className="bg-hz-cyan font-bold text-white hover:bg-hz-blue">
            <Globe className="mr-1 h-4 w-4" /> Publicar cambios
          </Button>
        </div>
      </div>

      {alerts.length > 0 && (
        <div className="rounded-xl border border-hz-warning/40 bg-hz-warning/5 p-3" data-testid="landing-alerts">
          {alerts.map((a) => (
            <p key={a} className="flex items-center gap-2 text-xs font-semibold text-hz-warning"><AlertTriangle className="h-3.5 w-3.5" /> {a}</p>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-1 rounded-xl bg-white p-1" role="tablist" aria-label="Secciones de la landing">
        {SECTION_TABS.map(([key, label]) => (
          <button key={key} role="tab" aria-selected={tab === key} onClick={() => setTab(key)} data-testid={`landing-tab-${key}`}
            className={`rounded-lg px-3 py-2 text-xs font-bold transition-colors ${tab === key ? "bg-hz-navy text-white" : "text-hz-navy hover:bg-hz-bg"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-hz-gray/40 bg-white p-5" data-testid={`landing-editor-${tab}`}>
        {editors[tab]}
      </div>

      <div className="rounded-xl border border-hz-gray/40 bg-hz-bg p-3">
        <Txt label="Resumen del cambio (opcional, se registra en auditoría)" value={summary} onChange={setSummary} testId="landing-change-summary" placeholder="Ej. Actualicé fecha y sede oficiales" />
      </div>

      {/* Publicar */}
      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent className="bg-white sm:max-w-md" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="text-lg font-bold text-hz-navy">Publicar cambios</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="font-semibold text-hz-ink">Secciones modificadas respecto a la versión publicada:</p>
            {changedSections.length ? (
              <ul className="list-inside list-disc text-hz-ink/80" data-testid="landing-publish-diff">
                {changedSections.map((k) => <li key={k}>{SECTION_LABELS[k]}</li>)}
              </ul>
            ) : (
              <p className="text-hz-ink/60" data-testid="landing-publish-nodiff">No hay diferencias con la versión publicada.</p>
            )}
            <Chk label="Confirmo que deseo publicar estos cambios en la landing pública" checked={publishConfirm} onChange={setPublishConfirm} testId="landing-publish-confirm" />
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setPublishOpen(false)} data-testid="landing-publish-cancel" className="font-semibold">Cancelar</Button>
              <Button onClick={doPublish} disabled={!publishConfirm} data-testid="landing-publish-submit" className="bg-hz-cyan font-bold text-white hover:bg-hz-blue">
                <Globe className="mr-1 h-4 w-4" /> Publicar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Historial */}
      <Dialog open={historyOpen} onOpenChange={(o) => { setHistoryOpen(o); if (!o) setRestoreId(null); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto bg-white sm:max-w-lg" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="text-lg font-bold text-hz-navy">Historial de versiones publicadas</DialogTitle></DialogHeader>
          <div className="space-y-2" data-testid="landing-history-list">
            {history.map((v) => (
              <div key={v.id} className="rounded-xl border border-hz-gray/40 p-3" data-testid={`landing-version-${v.version_number}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-bold text-hz-navy">
                    v{v.version_number} {v.status === "published" && <span className="ml-1 rounded-full bg-hz-success/10 px-2 py-0.5 text-[10px] font-bold text-hz-success">ACTIVA</span>}
                  </p>
                  <Button size="sm" variant="outline" onClick={() => setRestoreId(v.version_number)} data-testid={`landing-restore-${v.version_number}`} className="font-semibold">
                    <RefreshCw className="mr-1 h-3.5 w-3.5" /> Restaurar como borrador
                  </Button>
                </div>
                <p className="mt-1 text-xs text-hz-ink/60">
                  {v.published_at ? new Date(v.published_at).toLocaleString("es-MX") : "—"}{v.published_by_email ? ` · ${v.published_by_email}` : ""}
                </p>
                {v.change_summary && <p className="mt-1 text-xs text-hz-ink/80">{v.change_summary}</p>}
                {restoreId === v.version_number && (
                  <div className="mt-2 flex items-center gap-2 rounded-lg border border-hz-warning/40 bg-hz-warning/5 p-2">
                    <p className="flex-1 text-xs font-semibold text-hz-ink">¿Restaurar esta versión como nuevo borrador? No se elimina el historial.</p>
                    <Button size="sm" onClick={doRestore} data-testid="landing-restore-confirm" className="bg-hz-warning font-bold text-white hover:bg-hz-warning/90">Confirmar</Button>
                  </div>
                )}
              </div>
            ))}
            {!history.length && <p className="text-sm text-hz-ink/60">Sin versiones publicadas todavía.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLanding;
